import React, { useState, useEffect } from 'react';
import { useUXFeatures, useBusinessMetrics } from '../hooks/useStatsigFeatures';
import Onboarding from './Onboarding';

interface OnboardingFlowProps {
  onComplete: () => void;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const { features, experiments } = useUXFeatures();
  const { trackUserJourney, trackFeatureUsage } = useBusinessMetrics();
  
  // Get experiment values for A/B testing
  const bannerContent = experiments.onboarding.get('banner_content', 'default');
  const bannerStyle = experiments.onboarding.get('banner_style', 'standard');
  const stepCount = experiments.onboarding.get('step_count', 3);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    // Track onboarding journey start
    trackUserJourney('onboarding_started', {
      flow_version: features.newOnboarding ? 'new' : 'legacy',
      banner_variant: bannerContent,
      banner_style: bannerStyle,
      step_count: stepCount,
      timestamp: Date.now()
    });
  }, [features.newOnboarding, bannerContent, bannerStyle, stepCount, trackUserJourney]);

  const handleOnboardingComplete = () => {
    const completionTime = Date.now();
    const duration = completionTime - startTime;
    
    // Track onboarding completion
    trackUserJourney('onboarding_completed', {
      flow_version: features.newOnboarding ? 'new' : 'legacy',
      banner_variant: bannerContent,
      banner_style: bannerStyle,
      step_count: stepCount,
      completion_time: completionTime,
      duration_ms: duration
    });
    
    // Track feature usage duration
    trackFeatureUsage('onboarding_flow', duration);
    
    // Call the original onComplete handler
    onComplete();
  };

  // If new onboarding feature is disabled, use legacy flow
  if (!features.newOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  // Enhanced onboarding with experiment parameters
  return (
    <div className="onboarding-flow-wrapper">
      {/* Experimental banner content */}
      {bannerContent !== 'default' && (
        <div className={`onboarding-banner ${bannerStyle === 'modern' ? 'modern-style' : 'standard-style'}`}>
          {bannerContent === 'welcome' && (
            <div className="text-center p-4 mb-6 bg-linear-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h2 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-2">
                Welcome to CareerCard! 🎉
              </h2>
              <p className="text-blue-700 dark:text-blue-300">
                Let's get you set up with a personalized career tracking experience.
              </p>
            </div>
          )}
          {bannerContent === 'professional' && (
            <div className="text-center p-4 mb-6 bg-linear-to-r from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 rounded-lg border border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Professional Career Management
              </h2>
              <p className="text-gray-700 dark:text-gray-300">
                Build your comprehensive professional profile in just a few steps.
              </p>
            </div>
          )}
          {bannerContent === 'quick' && (
            <div className="text-center p-4 mb-6 bg-linear-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h2 className="text-xl font-semibold text-green-900 dark:text-green-100 mb-2">
                Quick Setup ⚡
              </h2>
              <p className="text-green-700 dark:text-green-300">
                Get started in under 5 minutes with our streamlined setup process.
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Pass experiment parameters to the existing Onboarding component */}
      <Onboarding 
        onComplete={handleOnboardingComplete}
        // These would be new optional props we could add to enhance the existing component
        experimentalStepCount={stepCount}
        bannerVariant={bannerContent}
        styleVariant={bannerStyle}
      />
      
      {/* Debug information (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-black/80 text-white p-3 rounded-lg text-xs font-mono">
          <div>Flow: {features.newOnboarding ? 'New' : 'Legacy'}</div>
          <div>Banner: {bannerContent}</div>
          <div>Style: {bannerStyle}</div>
          <div>Steps: {stepCount}</div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFlow; 